import { Stack, Typography } from "@mui/material";
import StyledButton, { ButtonStyle } from "../../components/StyledButton";
import useNavigateParams from "../../util/navigate";
import { Icons } from "../../image/Icons";
import { ColorHexCodes } from "../../util/Color";
import { PageContext } from "./PageContext";
import { useDeviceClass, ui } from "../../util/responsive";

type RouteButtonData = {
  style: ButtonStyle,
  icon?: string,
  text: string,
  path?: string
}

export default function TopPage({ pageContext }: { pageContext: PageContext }) {
  const navigate = useNavigateParams();
  const d = useDeviceClass();
  const api = pageContext.api;

  if (!api) {
    return <>IndexedDBが使用出来ません。ブラウザが対応していない場合があります。IndexedDBに対応している最新のブラウザを使用してください。</>
  }

  // モックアップ準拠の 3 カテゴリ構成 (rev2.15-16)。
  const Routines: RouteButtonData[] = [
    { style: "primary", icon: Icons.QRIcon, text: "発行", path: "issuance/" },
    { style: "primary", icon: Icons.TruckIcon, text: "出荷", path: "carry-out/" },
    { style: "primary", icon: Icons.GraphIcon, text: "搬入", path: "carry-in/" },
    { style: "primary", icon: Icons.HelmetIcon, text: "施工実施", path: "construction-execute/" },
  ];

  const Planning: RouteButtonData[] = [
    { style: "primary", icon: Icons.HelmetIcon, text: "施工管理", path: "construction-plan/" },
    { style: "primary", icon: Icons.LoupeIcon, text: "材料検索", path: "search-materials/" },
  ];

  // 旧「工事情報入力」(ConstructionWork)は廃止し、工事は「マスター編集」→「工事マスタ」(Work)へ一本化済み。
  const Managements: RouteButtonData[] = [
    { style: "text", icon: Icons.LedgerIcon, text: "帳票出力", path: "reports/" },
    { style: "text", icon: Icons.PencilIcon, text: "マスター編集", path: "masters/" },
    { style: "text", icon: Icons.SettingIcon, text: "設定", path: "settings/" },
  ];

  return (
    <Stack width="100%" alignItems="center">
      <Stack p={`${ui.menu.outerPad[d]}px`} maxWidth={ui.menu.maxWidth[d]} width="100%" gap={`${ui.menu.titleBlockGap[d]}px`}>
        <Stack alignItems="center" justifyItems="center" gap={`${ui.menu.headBlockGap[d]}px`}>
          <img width={ui.menu.logo[d]} height={ui.menu.logo[d]} src={Icons.OkumuraIcon} />
          <Typography fontSize={ui.menu.appTitleFont[d]} fontWeight="bold" lineHeight={1}>材料管理システム</Typography>
        </Stack>
        <Stack gap={`${ui.menu.sectionsGap[d]}px`}>
          <NavButtonGrid pageContext={pageContext} label="通常業務" data={Routines} navigate={navigate} />
          <NavButtonGrid pageContext={pageContext} label="計画業務" data={Planning} navigate={navigate} />
          <NavButtonGrid pageContext={pageContext} label="管理業務" data={Managements} navigate={navigate} />
        </Stack>
      </Stack>
    </Stack>
  );
}

function NavButtonGrid({ pageContext, label, data, navigate }: { pageContext: PageContext, label: string, data: RouteButtonData[], navigate: (pathname: string) => void }) {
  const d = useDeviceClass();
  return (
    <Stack gap={`${ui.menu.sectionInnerGap[d]}px`}>
      <Typography fontSize={ui.menu.sectionTitleFont[d]} fontWeight="bold" lineHeight={1}>{label}</Typography>
      <Stack gap={`${ui.menu.rowGap[d]}px`}>
        {Array.from({ length: Math.ceil(data.length / 3) }).map((_, lineIndex) => {
          return (
            <Stack key={`navline-${lineIndex}`} direction="row" gap={`${ui.menu.colGap[d]}px`}>
              {Array.from({ length: 3 }).map((_, index) => {
                  if (data.length > (lineIndex * 3) + index) {
                    return <NavButton key={`navbutton-${index}`} pageContext={pageContext} buttonData={data[(lineIndex * 3) + index]} navigate={navigate} />
                  }
                  return <NavButton key={`navbutton-${index}`} pageContext={pageContext} navigate={navigate} />
              })}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  )
}

function NavButton({ pageContext, buttonData, navigate }: { pageContext: PageContext, buttonData?: RouteButtonData, navigate: (pathname: string) => void }) {
  const d = useDeviceClass();
  const buttonSx = { px: 2, flex: 1, boxShadow: 3, backgroundColor: `#${ColorHexCodes.White}` };

  if (!buttonData) {
    return (
      <Stack flex={1} px={2}/>
    )
  }

  function handleClick() {
    if (!buttonData?.path) {
      pageContext.api.showError("こちらは現在未実装の機能です。");
      return;
    }
    navigate(buttonData.path);
  }

  return (
    <StyledButton buttonStyle={buttonData.style} size="large" onClick={handleClick} sx={buttonSx}>
      <Stack alignContent="center" gap={`${ui.menu.buttonInnerGap[d]}px`} height={ui.menu.buttonHeight[d]} justifyContent="center" pt={1.5}>
        <img height={ui.menu.buttonIcon[d]} src={buttonData.icon} style={{ objectFit: "contain" }} />
        <Typography fontSize={ui.menu.buttonLabelFont[d]} fontWeight="bold">{buttonData.text}</Typography>
      </Stack>
    </StyledButton>
  );
}
